/**
 * @fileoverview The weather page: now, what the week means for the garden, the week against
 * its normals, and the frost dates.
 *
 * It listed every plant with its own verdicts, seven cards saying the same
 * three lines under a "worth knowing" heading. The garden's week is the same
 * lines the Plants page's filter sheet offers, the Today brief's
 * (todo.week.lines, the week by condition from the list's own weather,
 * finished by the backend; the owner's rule: the backend is for business
 * logic, the front end is display): a row each, in the server's words
 * ("Too cold Fri–Tue nights"), with how many plants it is for
 * and its own icon. A row opens in place to name every one of those plants,
 * each as the server titles it (lib/plantName plantShown: two of one name
 * told apart by their variety, kind, bed or the day added, never by the app;
 * audit A36), and a name opens that plant's card over this page, the card the grid and
 * the locations open. It used to leave for the plant list filtered to them,
 * which took the forecast away to answer "which three?"; closing the card
 * now leaves you where you were. Before the brief the rows were the cards'
 * marks gathered in the app (gardenMarks).
 *
 * Reads the same Garden Outlook the plant cards read for the week. It used
 * to derive its own verdicts from crop_temperatures while the cards used
 * the pipeline, so the two screens could disagree about the same plant on
 * the same day — and the page had no notion of watering at all.
 *
 * Organised by what the week demands rather than by plant, because that is what
 * this page is for; the cards already answer how one plant is doing. Each row
 * carries the headlines and links to the card for the reasoning behind them.
 *
 * The forecast can be linked to, /weather#forecast, which is where Today's
 * Coming up lines and the bar's garden-wide lines lead; arriving by it
 * scrolls it into view (useArrivalScroll).
 *
 * Where: the route /weather (the Weather tab).
 * API: GET /weather/forecast/user (useWeatherDays: the server's days for the bare week, and the issuing office; a garden
 * the weather service cannot place yet gets the server's own sentence, noForecastMessage, B44); GET /header (the weather
 * now, useWeatherNow: the reading, the sky's words and icon key, all the server's; W24 Job D);
 * GET /plants/outlook?zip=&tempUnit= (usePlantOutlook); GET /today/brief
 * (useTodayBrief: the garden's week, and the forecast week judged, weatherWeek,
 * which ForecastWeek draws; audit batch 4, A24); the plants (usePlantsContext: GET
 * /plants); FrostOutlook's own call; the quick look's card calls.
 * Settings: the gardener's temperature unit (the server says the readings in it); a plant's bed
 * marked sheltered leaves it out of the garden's rows (the brief's rule).
 * Words (en/es): weather.title, weather.currentConditions, weather.loading,
 * weather.loadingOutlook, weather.error, weather.gardenWeek,
 * weather.noPlants, weather.needsPostalCode, weather.wateringWeek,
 * weather.bar.* (plants, plantsOne, allClear), weather.stats.lowTonight,
 * weather.week.high, weather.week.rain, weather.next7, weather.sources.*;
 * plants.goToToday.
 * Watch out: the page waits only for what it cannot draw without: with the
 * outlook in, a slow or failed bare forecast does not blank it. When
 * neither came and one failed on our side (a 500, no connection; D31,
 * lib/serverFailure), the page says the standard sentence, reported to
 * Sentry, with the support address as a link, instead of weather.error; a
 * brief that failed says it in the garden's week alone.
 *
 * @module WeatherPage
 */

import React from 'react';
import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { usePlantsContext } from '@/shared/hooks/usePlantsContext';
import { usePlantOutlook } from '@/shared/hooks/usePlantOutlook';
import { useWeatherNow } from '@/shared/hooks/useWeatherNow';
import { useTodayBrief } from '@/shared/hooks/useTodayBrief';
import { SkyIcon } from '@/shared/components/SkyIcon';
import { GrowLoader } from '@/shared/components/ui/GrowLoader';
import { GuidanceQuiet, GuidanceUpdating } from '@/shared/components/GuidanceUpdating';
import { ServerFailureNote } from '@/shared/components/ServerFailure';
import { isServerFailure } from '@/shared/lib/serverFailure';
import type { BriefWeekLine } from '@/shared/api/todayBrief';
import { weekLineIcon, weekLineInk } from '@/features/plant-list/lib/weekLineLook';
import { PlantQuickLook } from '@/features/plant-list/components/PlantQuickLook';
import { useArrivalScroll } from '@/shared/hooks/useArrivalScroll';
import { useWeatherDays } from '../useWeatherDays';
import { ForecastWeek } from './ForecastWeek';
import { FrostOutlook } from './FrostOutlook';
import { plantShown } from '@/shared/lib/plantName';

/** Now, the garden's week, the forecast against its normals, and the frost dates. */
export const WeatherPage: React.FC = () => {
  const { reactTranslate: t } = useTranslation();
  // The forecast's days and the issuing office, the server's (the bare week, when the outlook is missing; the footer).
  const { data: forecastDays, isLoading: weatherLoading, error: weatherError } = useWeatherDays();
  const noForecastMessage = forecastDays?.noForecastMessage ?? null;
  /** The server's days, up to seven: the bare week. */
  const week = forecastDays?.days ?? [];
  /** Whether the server sent a forecast at all (none for a garden it cannot place yet). */
  const forecast = week.length > 0;
  const { plants, isLoading: plantsLoading } = usePlantsContext();
  const { conditions, isLoading: outlookLoading, error: outlookError, missingZip } = usePlantOutlook();
  // The garden's week: the brief's lines, the same the Plants page's bar says. Not asked for without a postcode Grow can plan from.
  const { brief, isLoading: briefLoading, isError: briefFailed, error: briefError, noZip } = useTodayBrief();
  // Arrived by /weather#forecast (Today's Coming up): to the forecast once it is drawn.
  useArrivalScroll('forecast', !weatherLoading && !outlookLoading);

  /** The garden's week: the list's lines (todo.week.lines: the week by condition, the water line last), in its order. */
  const verdicts = React.useMemo((): BriefWeekLine[] => brief?.todo.week.lines ?? [], [brief]);

  /*
   * Which rows are open. The dangers start open, as they lead the Plants
   * page's weather bar: the one that needs doing tonight should not wait
   * for a tap. Only on the first answer, so closing it stays closed.
   */
  const [openKeys, setOpenKeys] = React.useState<Set<string> | null>(null);
  React.useEffect(() => {
    if (openKeys !== null || verdicts.length === 0) return;
    setOpenKeys(new Set(verdicts.filter((v) => v.danger).map((v) => v.id)));
  }, [openKeys, verdicts]);
  /**
   * Opens a row, or closes it.
   *
   * @param key - The row's key (the line's id, e.g. 'grow_care_cold', 'water').
   */
  const toggle = (key: string): void => setOpenKeys((was) => {
    const next = new Set(was ?? []);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // The plant whose card is open over the page, if any.
  const [openedId, setOpenedId] = React.useState<number | null>(null);

  /** The garden's plants by id: the rows name them, and a name opens its card. */
  const plantById = React.useMemo(
    () => new Map((plants ?? []).map((plant) => [plant.plant_obj_id, plant])),
    [plants]
  );


  /*
   * The National Weather Service office the forecast is issued by, named and
   * with its identifier: "Nashville, TN (OHX)". Absent for anyone outside the
   * US, whose forecast comes from elsewhere and has no office to name.
   */
  const office = forecastDays?.office ?? null;
  const station = office?.name
    ? (office.id ? `${office.name} (${office.id})` : office.name)
    : null;
  /**
   * Right now: the reading every page shows (useWeatherNow, the header
   * answer's): the reading, the sky's words and its icon key, all the server's.
   */
  const now = useWeatherNow();

  // The weather's rows, and watering apart: it is the plants' week, not the weather's.
  const weatherRows = verdicts.filter((verdict) => !verdict.water);
  const waterRows = verdicts.filter((verdict) => verdict.water);
  /**
   * One of the garden's rows: its title and how many plants, opening to name them.
   *
   * @param verdict - One of the week's lines.
   * @returns The row, and its names when open.
   */
  const verdictRow = (verdict: BriefWeekLine): JSX.Element => {
    const ink = weekLineInk(verdict);
    // Its icon as the Plants page's bar draws it (weekLineLook).
    const Icon = weekLineIcon(verdict);
    const count = verdict.plantIds.length;
    const open = openKeys?.has(verdict.id) ?? false;
    const panelId = `verdict-${verdict.id}`;
    const named = verdict.plantIds
      .map((id) => plantById.get(id))
      .filter((plant): plant is NonNullable<typeof plant> => Boolean(plant));
    return (
      <li key={verdict.id}>
        {/*
          * One arrow, and only here: down while closed, turned up
          * while open, the way a fold reads everywhere. The rows used
          * to end in a right-pointing arrow and lead off the page.
          */}
        <button
          type="button"
          onClick={() => toggle(verdict.id)}
          aria-expanded={open}
          aria-controls={panelId}
          className="flex min-h-11 w-full items-center gap-2.5 py-1.5 text-left transition-colors hover:bg-muted/50"
        >
          <Icon className={cn('h-4 w-4 shrink-0', ink)} aria-hidden="true" />
          <span className={cn('min-w-0 flex-1 text-base font-semibold', ink)}>
            {verdict.title}
          </span>
          <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
            {count === 1 ? t('weather.bar.plantsOne') : t('weather.bar.plants', { count })}
          </span>
          <ChevronDown
            className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none', open && 'rotate-180')}
            aria-hidden="true"
          />
        </button>
        {open && (
          /* Every plant, by name. Opening the verdict is the tap that
             asked to see them, so none are held back behind a
             second one. A name opens its card over this page. */
          <div id={panelId} className="flex flex-wrap gap-1.5 pb-3 pl-[1.625rem]">
            {/* Each by the server's title: two of one name told apart by what differs (variety, kind, bed, day added). */}
            {named.map((plant) => (
              <button
                key={plant.plant_obj_id}
                type="button"
                onClick={() => setOpenedId(plant.plant_obj_id)}
                className="inline-flex min-h-8 items-center rounded-md border bg-background px-2.5 text-sm hover:bg-accent"
              >
                {plantShown(plant)}
              </button>
            ))}
          </div>
        )}
      </li>
    );
  };

  /*
   * Waiting, or failed, only for what the page cannot draw without. The
   * outlook carries the week, its normals and the reading now, so the
   * forecast is needed only where there is no outlook, and a missing
   * forecast waits for the outlook that could stand in for it. It used to
   * wait on the forecast whatever happened, so a slow or failed forecast
   * blanked a page whose outlook had already arrived.
   */
  if (plantsLoading || (!conditions && (weatherLoading || (outlookLoading && !forecast)))) {
    return (
      <GrowLoader variant="screen" steps={[t('loader.weather.check'), t('loader.weather.week')]} />
    );
  }

  if (!conditions && (weatherError || !forecast)) {
    // Either failure on our side is the standard sentence; the outlook's first, as the page's own.
    const ours = [outlookError, weatherError].find(isServerFailure);
    if (ours !== undefined) {
      return (
        <div className="py-16 text-center">
          <ServerFailureNote error={ours} where="Weather" className="text-destructive" />
        </div>
      );
    }
    // The weather service cannot place the garden yet (B44): the server's sentence, which says it has been reported.
    if (noForecastMessage) {
      return (
        <div className="py-16 text-center text-sm text-muted-foreground">
          {noForecastMessage}
        </div>
      );
    }
    return (
      <div className="py-16 text-center text-sm text-destructive">
        {t('weather.error')}
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      <h1 className="text-2xl font-semibold text-foreground font-body">
        {t('weather.title')}
      </h1>

      {/* Current conditions, stated once rather than on every plant card */}
      {now && (
        <section className="rounded-lg border border-border bg-card p-4">
          <h2 className="font-body text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
            {/* The period's own name when there is one: at dusk "Tonight" is a
                truer heading than "Right now" for a forecast that runs to
                morning. */}
            {now.label || t('weather.currentConditions')}
          </h2>
          <div className="flex items-center gap-3 flex-wrap">
            {/* The sky's key, the server's, in the app's standard icons (SkyIcon). */}
            <SkyIcon sky={now.icon} className="h-12 w-12 shrink-0 text-muted-foreground" strokeWidth={1.5} aria-hidden="true" />
            {now.display && (
              <span className="text-3xl font-bold">{now.display}</span>
            )}
            {/* The sky in the gardener's language, as the server says it (one sentence casing everywhere). */}
            <span className="text-sm text-muted-foreground">
              {now.words}
            </span>
          </div>

          {/* Tonight's low first: it is the reading that decides whether to cover
              anything. Then the day's high and its chance of rain. */}
          {conditions?.days?.[0] && (
            <p className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
              {conditions.days[0].min !== null && (
                <span>{t('weather.stats.lowTonight')} <b className="font-semibold text-foreground">{Math.round(conditions.days[0].min)}°</b></span>
              )}
              {conditions.days[0].max !== null && (
                <span>{t('weather.week.high')} <b className="font-semibold text-foreground">{Math.round(conditions.days[0].max)}°</b></span>
              )}
              {(conditions.days[0].pop ?? 0) > 0 && (
                <span>{t('weather.week.rain')} <b className="font-semibold text-foreground">{conditions.days[0].pop}%</b></span>
              )}
            </p>
          )}
        </section>
      )}

      {/* "Updating your garden..." while a fresh brief is on its way; the old week below looks quieter until it arrives. */}
      <GuidanceUpdating where="Weather" />

      {/* What the week means for the garden: the plant list's own rows. */}
      <GuidanceQuiet>
      <section className="rounded-lg border border-border bg-card p-4">
        <h2 className="mb-1 text-base font-semibold font-body">{t('weather.gardenWeek')}</h2>
        {!plants?.length ? (
          <p className="text-sm text-muted-foreground">{t('weather.noPlants')}</p>
        ) : missingZip || noZip ? (
          <p className="text-sm text-muted-foreground">
            {t('weather.needsPostalCode')}{' '}
            {/* Today is where the postcode is asked for (one Grow cannot plan from is asked for again). */}
            <Link to="/today" className="font-medium underline underline-offset-2">{t('plants.goToToday')}</Link>
          </p>
        ) : outlookLoading || briefLoading ? (
          <GrowLoader variant="block" label={t('weather.loadingOutlook')} />
        ) : briefFailed || (brief !== undefined && !brief.hasOutlook) ? (
          // The week could not be read: a failure on our side says the standard sentence; any other, weather.error.
          isServerFailure(briefError)
            ? <ServerFailureNote error={briefError} where="Weather week" className="text-muted-foreground" />
            : <p className="text-sm text-muted-foreground">{t('weather.error')}</p>
        ) : weatherRows.length === 0 ? (
          <p className="text-sm text-verdict-good">{t('weather.bar.allClear')}</p>
        ) : (
          <ul className="divide-y">
            {weatherRows.map(verdictRow)}
          </ul>
        )}
        {waterRows.length > 0 && !outlookLoading && !briefLoading && !missingZip && !noZip && (
          <div className="mt-3 border-t pt-2">
            <h3 className="font-body text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('weather.wateringWeek')}</h3>
            <ul className="divide-y">{waterRows.map(verdictRow)}</ul>
          </div>
        )}
      </section>
      </GuidanceQuiet>

      {/* The full forecast, in its rightful place. The outlook's own copy
          carries the normals each day is measured against; the bare grid below
          is the fallback for when no outlook could be fetched. */}
      {/* #forecast: where Today's Coming up lines lead. */}
      <div id="forecast" className="scroll-mt-20">
      {conditions ? (
        <ForecastWeek conditions={conditions} said={brief?.weatherWeek ?? null} />
      ) : (
      <section>
        <h2 className="text-sm font-medium mb-2">{t('weather.next7')}</h2>
        <div className="overflow-x-auto">
          <div className="grid grid-cols-7 gap-2 min-w-[560px]">
            {week.map((day) => (
              <div
                key={day.date}
                className="rounded-md border border-border p-2 text-center"
              >
                {/* The day's short name, the server's, in the gardener's language ("Sat", "sáb"). */}
                <div className="text-xs font-medium mb-1">{day.label}</div>
                {/* The day's sky key, else its night's (a day the forecast opens on in the evening), the server's. */}
                <SkyIcon sky={day.day?.icon ?? day.night?.icon} className="mx-auto mb-1 h-5 w-5 text-muted-foreground" strokeWidth={1.5} aria-hidden="true" />
                {/* A dash for a high the forecast no longer has (today, in the evening), never tomorrow's,
                    or for one it has no reading for. A night with no reading is left out, like no night. */}
                <div className="text-sm font-semibold">{day.day?.display || '–'}</div>
                {day.night?.display && (
                  <div className="text-xs text-muted-foreground">{day.night.display}</div>
                )}
                {day.day?.words && (
                  <div className="mt-1 text-[10px] leading-tight text-muted-foreground">
                    {day.day.words}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
      )}
      </div>

      {/* When frost is coming, and how firm that date is. It belongs with the
          weather rather than on the plant grid: it is a fact about this place,
          not about any one plant. */}
      <FrostOutlook />

      {/* Where it all comes from, once, at the foot. */}
      <p className="text-xs text-muted-foreground">
        {station ? t('weather.sources.forecastAt', { station }) : t('weather.sources.forecast')}{' '}
        {t('weather.sources.normals')}
      </p>

      <PlantQuickLook plant={openedId === null ? null : plantById.get(openedId) ?? null} onClose={() => setOpenedId(null)} />
    </div>
  );
};
